// client/src/components/wholesaler/ProductLimitBanner.js
// Location: C:\Users\chari\Desktop\wholesale-platform\client\src\components\wholesaler\ProductLimitBanner.js
// 
// Product Limit Warning Banner Component
// Shows tiered warnings based on product usage (75%, 90%, 100%)
// Displays upgrade prompts with correct tier information from pricingTiers.js
//
// Props:
// - currentCount: number - Current number of products
// - limit: number - Product limit for current tier (-1 = unlimited)
// - tier: string - Current subscription tier (starter/professional/enterprise)
// - onUpgradeClick: function - Callback when upgrade button clicked
//
// Features:
// - Visual indicators at 75%, 90%, 100% usage
// - Progress bars with color coding
// - Next tier recommendations with accurate pricing
// - Bilingual support (Greek/English) using i18next
// - Mobile-responsive design

import React from 'react';
import { useTranslation } from 'react-i18next';

const ProductLimitBanner = ({ currentCount, limit, tier, onUpgradeClick }) => {
  const { t } = useTranslation(['wholesaler', 'common']);
  
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : Math.round((currentCount / limit) * 100);
  const remaining = isUnlimited ? 'Unlimited' : limit - currentCount;
  
  const isAtLimit = percentage >= 100;
  const isNearLimit = percentage >= 90 && percentage < 100;
  const isWarning = percentage >= 75 && percentage < 90;

  // Don't show banner if well below limit (< 75%)
  if (isUnlimited || percentage < 75) {
    return null;
  }

  const getTierName = (tier) => {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  // Updated to match pricingTiers.js: 1500 / 5000 / Unlimited
  const getNextTier = () => {
    if (tier === 'starter') {
      return { 
        name: 'Professional', 
        limit: '5,000', 
        limitNumber: 5000,
        price: '€129',
        perOrderFee: '€0.25',
        features: [
          t('subscription.features.professionalProducts', 'Up to 5,000 products'),
          t('subscription.features.advancedAnalytics', 'Advanced analytics'),
          t('subscription.features.bulkUpload', 'Bulk CSV upload'),
          t('subscription.features.lowerFees', 'Lower per-order fees (€0.25)')
        ]
      };
    }
    if (tier === 'professional') {
      return { 
        name: 'Enterprise', 
        limit: t('subscription.unlimited', 'Unlimited'), 
        limitNumber: -1,
        price: '€299',
        perOrderFee: '€0.15',
        features: [
          t('subscription.features.unlimitedProducts', 'Unlimited products'),
          t('subscription.features.multiWarehouse', 'Multi-warehouse support'),
          t('subscription.features.apiAccess', 'API access'),
          t('subscription.features.lowestFees', 'Lowest per-order fees (€0.15)')
        ]
      };
    }
    return null;
  };

  const nextTier = getNextTier();

  // AT LIMIT - 100% (Red Alert)
  if (isAtLimit) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 text-4xl">🚫</div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-red-800 mb-2">
              {t('subscription.limitReached', 'Product Limit Reached')}
            </h3>
            <p className="text-red-700 mb-4">
              {t('subscription.limitReachedMessage', {
                defaultValue: "You've used all {{limit}} product slots on your {{tier}} plan. You cannot add more products until you upgrade.",
                limit: limit,
                tier: getTierName(tier)
              })}
            </p>
            
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-red-700 font-medium">
                  {currentCount} / {limit} {t('subscription.products', 'products')}
                </span>
                <span className="text-red-800 font-bold">100%</span>
              </div>
              <div className="w-full bg-red-200 rounded-full h-3">
                <div className="bg-red-600 h-3 rounded-full w-full"></div>
              </div>
            </div>

            {nextTier && (
              <div className="bg-white rounded-lg p-4 border border-red-200 mb-4">
                <h4 className="font-semibold text-gray-800 mb-2">
                  ✨ {t('subscription.upgradeTo', { tier: nextTier.name }, `Upgrade to ${nextTier.name}`)}
                </h4>
                <ul className="text-sm text-gray-700 space-y-1 mb-3">
                  {nextTier.features.map((feature, index) => (
                    <li key={index}>• <strong>{feature}</strong></li>
                  ))}
                  <li>• {t('subscription.onlyPerMonth', { price: nextTier.price }, `Only ${nextTier.price}/month`)}</li>
                </ul>
              </div>
            )}

            <button
              onClick={onUpgradeClick}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold w-full sm:w-auto"
            >
              {t('subscription.viewUpgradePlans', 'View Upgrade Plans')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // NEAR LIMIT - 90-99% (Yellow Warning)
  if (isNearLimit) {
    return (
      <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 text-4xl">⚠️</div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-yellow-800 mb-2">
              {t('subscription.almostAtLimit', 'Almost at Limit!')}
            </h3>
            <p className="text-yellow-700 mb-4">
              {t('subscription.almostAtLimitMessage', {
                defaultValue: 'Only {{remaining}} product slots remaining out of {{limit}}.{{upgradeMessage}}',
                remaining: remaining,
                limit: limit,
                upgradeMessage: nextTier ? ` Upgrade to ${nextTier.name} for ${nextTier.limit} slots!` : ''
              })}
            </p>
            
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-yellow-700 font-medium">
                  {currentCount} / {limit} {t('subscription.products', 'products')}
                </span>
                <span className="text-yellow-800 font-bold">{percentage}%</span>
              </div>
              <div className="w-full bg-yellow-200 rounded-full h-3">
                <div 
                  className="bg-yellow-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>

            {nextTier && (
              <div className="bg-white rounded-lg p-4 border border-yellow-200 mb-4">
                <h4 className="font-semibold text-gray-800 mb-2">
                  ✨ {t('subscription.upgradeTo', { tier: nextTier.name }, `Upgrade to ${nextTier.name}`)}
                </h4>
                <ul className="text-sm text-gray-700 space-y-1 mb-3">
                  {nextTier.features.map((feature, index) => (
                    <li key={index}>• <strong>{feature}</strong></li>
                  ))}
                  <li>• {t('subscription.onlyPerMonth', { price: nextTier.price }, `Only ${nextTier.price}/month`)}</li>
                </ul>
              </div>
            )}

            <button
              onClick={onUpgradeClick}
              className="bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors font-semibold w-full sm:w-auto"
            >
              {t('subscription.viewUpgradePlans', 'View Upgrade Plans')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // WARNING - 75-89% (Blue Info)
  if (isWarning) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 text-2xl">💡</div>
          <div className="flex-1">
            <h4 className="font-semibold text-blue-800 mb-1">
              {t('subscription.gettingCloseToLimit', 'Getting close to your limit')}
            </h4>
            <p className="text-sm text-blue-700 mb-3">
              {t('subscription.gettingCloseMessage', {
                defaultValue: "You're using {{currentCount}} of {{limit}} product slots ({{percentage}}%).{{upgradeMessage}}",
                currentCount: currentCount,
                limit: limit,
                percentage: percentage,
                upgradeMessage: nextTier ? ` Consider upgrading to ${nextTier.name} for more capacity.` : ''
              })}
            </p>
            
            {/* Compact Progress Bar */}
            <div className="mb-3">
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>

            <button
              onClick={onUpgradeClick}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
            >
              {t('subscription.viewUpgradeOptions', 'View upgrade options')} →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ProductLimitBanner;